/**
 * CallRail Integration Module (Story 22.1)
 *
 * Barrel export + side-effect adapter registration.
 *
 * Registration happens here — NOT in `callrail.ts` — to prevent the
 * Failure 5a anti-pattern documented in `google/index.ts:7`. Importing
 * this module is sufficient to register the adapter; importing
 * `callrail.ts` directly would give you the adapter object without
 * registration.
 */

import { registerAdapter } from "../registry";
import { callRailAdapter } from "./callrail";

// Side-effect registration (Story 22.1)
registerAdapter("callrail", callRailAdapter);

// Public surface — adapter + canonical constants
export {
  callRailAdapter,
  CALLRAIL_PLATFORM_VALUES,
  CALLRAIL_METRICS,
  CALLRAIL_DIMENSIONS,
  CALLRAIL_API_VERSION,
  CALLRAIL_API_BASE,
  CALLRAIL_FRESHNESS_LAG_HOURS,
  sanitizeCallRailPii,
  // Story 22.5 — public KV-side-channel readers for the recording routes
  readCallRailRecordingsEnabled,
  type CallRailPlatform,
} from "./callrail";

// API helpers
export {
  discoverCallRailAccounts,
  validateCallRailConnection,
  probeCallRailConversationIntelligence,
  // Story 22.5 — recording-related upstream probes + fetchers
  probeCallRailRecordingsEnabled,
  fetchCallRailRecordingUrl,
  type FetchCallRailRecordingUrlResult,
  fetchCallRailCallsSummary,
  fetchCallRailCallsTimeseries,
  fetchCallRailCalls,
  fetchCallRailFormSubmissions,
  fetchCallRailTextMessages,
  CALLRAIL_PER_PAGE_MAX,
  type CallRailAccount,
  type DiscoverCallRailAccountsResult,
  type ProbeCallRailCiResult,
  type CallRailCiEntitlement,
  type FetchCallRailCallsSummaryParams,
  type FetchCallRailCallsTimeseriesParams,
  type FetchCallRailCallsParams,
  type FetchCallRailFormSubmissionsParams,
  type FetchCallRailTextMessagesParams,
  type CallRailSummaryResponse,
  type CallRailTimeseriesResponse,
  type CallRailCallsResponse,
  type CallRailFormSubmissionsResponse,
  type CallRailTextMessagesResponse,
} from "./api";

// Errors
export {
  parseCallRailApiError,
  callRailErrorTypeToAdapterCode,
  sanitizeCallRailErrorMessage,
  CallRailApiCallError,
  type CallRailApiError,
  type CallRailErrorType,
} from "./errors";

// Safe fetch
export {
  safeFetchCallRail,
  buildCallRailAuthHeader,
  CALLRAIL_429_COOLDOWN_MS,
} from "./safe-fetch";

// Cache policy
export {
  CALLRAIL_ENDPOINT_CACHE_TTL,
  getCallRailEndpointCacheTtl,
  buildCallRailEndpointCacheKey,
  type CallRailCacheableEndpoint,
} from "./cache-policy";

// Retention
export {
  CALLRAIL_RETENTION_MONTHS,
  CallRailRetentionViolationError,
  computeCallRailRetentionCutoff,
  validateCallRailDateRangeAgainstRetention,
} from "./retention";

// Fields
export {
  CALLRAIL_FIELD_ALLOWLIST,
  CALLRAIL_CI_GATED_FIELDS,
  CALLRAIL_SUMMARY_FILTER_KEYS,
  CALLRAIL_GROUP_BY_VALUES,
  CALLRAIL_GRANULARITY_VALUES,
  CALLRAIL_TIMESERIES_MAX_POINTS,
  computeCallRailFields,
} from "./fields";

// PII
export {
  CALLRAIL_PII_FIELDS,
  CALLRAIL_PII_FIELDS_CAMEL,
  stripCsvInjectionPrefix,
} from "./pii";

// Transformers
export {
  transformCallRailSummaryRow,
  transformCallRailTimeseriesRow,
  transformCallRailCallRow,
  transformCallRailFormSubmissionRow,
  transformCallRailTextMessageRow,
  type CallRailReportType,
  type CallRailSummaryApiRow,
  type CallRailTimeseriesApiRow,
  type CallRailCallApiRow,
  type CallRailFormSubmissionApiRow,
  type CallRailTextMessageApiRow,
} from "./transformers";

// New paginating wrappers (Pass 2 review fix)
export {
  fetchAllCallRailCalls,
  fetchAllCallRailFormSubmissions,
  fetchAllCallRailTextMessages,
} from "./api";
